/**
 * Page publique d'un article de loi
 * Routes :
 *   /articles/:slugCode/:numero       ← route principale SEO
 *   /articles/by-node-id/:nodeId      ← lookup direct Qdrant
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import { PublicHeader, PublicFooter, FicheCard, MCPBadge, type PublicFiche } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';
import { AdvancedSearchPanel, type AdvancedFilters, EMPTY_ADV_FILTERS } from '@/components/jurisprudence/AdvancedSearch';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ArticleDetail {
  legiarti_id:     string;
  code:            string;
  slug_code:       string;
  numero:          string;
  nature:          string;
  classification:  string;
  text:            string;
  section_summary: string;
  questions:       string[];
  legifrance_url:  string;
  canonical_url:   string;
  date_debut?:     string | null;
  date_fin?:       string | null;
  en_vigueur?:     boolean | null;
}

type ArticleTab = 'text' | 'questions' | 'fiches';

const SITE_URL = 'https://smartlawyer.ai';
const LIMIT    = 10;

export default function PublicArticle() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const { slugCode, numero, nodeId } = useParams<{
    slugCode?: string;
    numero?:   string;
    nodeId?:   string;
  }>();

  // ?date=YYYY-MM-DD : version applicable à cette date (lien depuis une fiche)
  const [searchParams] = useSearchParams();
  const versionDate    = searchParams.get('date');

  const apiUrl = nodeId
    ? `/api/articles/by-node-id/${nodeId}`
    : `/api/articles/${slugCode}/${numero}${versionDate ? `?date=${encodeURIComponent(versionDate)}` : ''}`;

  const [article, setArticle]     = useState<ArticleDetail | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [activeTab, setActiveTab] = useState<ArticleTab>('text');

  const [fiches, setFiches]               = useState<PublicFiche[]>([]);
  const [fichesTotal, setFichesTotal]     = useState(0);
  const [loadingFiches, setLoadingFiches] = useState(false);
  const [fichesPage, setFichesPage]       = useState(0);
  const cursorsRef = useRef<(string | null)[]>([null]);

  const [searchQuery, setSearchQuery]     = useState('');
  const [searchInput, setSearchInput]     = useState('');
  const [searchResults, setSearchResults] = useState<PublicFiche[]>([]);
  const [searchTotal, setSearchTotal]     = useState(0);
  const [searching, setSearching]         = useState(false);
  const [searchDone, setSearchDone]       = useState(false);
  const [searchTime, setSearchTime]       = useState<number | null>(null);

  const [showFilters, setShowFilters]     = useState(false);
  const [filters, setFilters]             = useState<AdvancedFilters>(EMPTY_ADV_FILTERS);
  const [activeFilters, setActiveFilters] = useState<AdvancedFilters>(EMPTY_ADV_FILTERS);

  const hasActiveFilters = Object.entries(activeFilters).some(([k, v]) =>
    k !== 'enriched_only' && k !== 'quality_min' ? !!v : v !== false && v !== ''
  );

  useEffect(() => {
    setLoading(true);
    setError(false);
    setArticle(null);
    setFiches([]);
    setFichesTotal(0);
    setFichesPage(0);
    setActiveTab('text');
    setSearchQuery('');
    setSearchInput('');
    setSearchResults([]);
    setSearchDone(false);
    setFilters(EMPTY_ADV_FILTERS);
    setActiveFilters(EMPTY_ADV_FILTERS);
    cursorsRef.current = [null];

    fetch(apiUrl)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setArticle(data);
        const params = new URLSearchParams({ slug_code: data.slug_code, numero: data.numero, limit: '1' });
        fetch(`/api/fiches/by-article?${params}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setFichesTotal(d.total || 0); })
          .catch(() => {});
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [apiUrl]);

  useEffect(() => {
    if (activeTab !== 'fiches' || !article || searchDone) return;
    setLoadingFiches(true);

    const cursor = cursorsRef.current[fichesPage] ?? null;
    const params = new URLSearchParams({
      slug_code: article.slug_code,
      numero:    article.numero,
      limit:     String(LIMIT),
    });
    if (activeFilters.jurisdiction) params.set('jurisdiction', activeFilters.jurisdiction);
    if (activeFilters.solution)     params.set('solution',     activeFilters.solution);
    if (activeFilters.date_from)    params.set('date_from',    activeFilters.date_from);
    if (activeFilters.date_to)      params.set('date_to',      activeFilters.date_to);
    if (cursor)                     params.set('cursor',       cursor);

    fetch(`/api/fiches/by-article?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setFiches(data.results || []);
        setFichesTotal(data.total || 0);
        if (data.next_cursor != null) {
          cursorsRef.current[fichesPage + 1] = data.next_cursor;
        }
      })
      .catch(console.error)
      .finally(() => setLoadingFiches(false));
  }, [activeTab, article, fichesPage, activeFilters, searchDone]);

  const doSearch = useCallback(async (q: string, f: AdvancedFilters) => {
    if (!article || !q.trim()) return;
    setSearching(true);
    setSearchDone(false);
    setSearchResults([]);
    const t0 = performance.now();
    try {
      const body: Record<string, unknown> = {
        query:          q,
        limit:          100,
        article_code:   article.code,
        article_numero: article.numero,
      };
      const filterMap: Record<string, string> = {};
      if (f.jurisdiction) filterMap.jurisdiction = f.jurisdiction;
      if (f.solution)     filterMap.solution     = f.solution;
      if (f.date_from)    filterMap.date_from    = f.date_from;
      if (f.date_to)      filterMap.date_to      = f.date_to;
      if (Object.keys(filterMap).length) body.filters = filterMap;

      const res  = await fetch('/api/fiches/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      setSearchResults(data.results || []);
      setSearchTotal(data.total    || 0);
      setSearchTime(Math.round(performance.now() - t0));
      setSearchDone(true);
      setSearchQuery(q);
    } catch (e) {
      console.error('doSearch error:', e);
    } finally {
      setSearching(false);
    }
  }, [article]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) doSearch(searchInput, activeFilters);
  };

  const handleSearchReset = () => {
    setSearchQuery(''); setSearchInput(''); setSearchResults([]);
    setSearchDone(false); setSearchTime(null);
    setFichesPage(0); cursorsRef.current = [null];
  };

  const handleFiltersApply = () => {
    setActiveFilters(filters); setShowFilters(false);
    setFichesPage(0); cursorsRef.current = [null];
    setFiches([]); setFichesTotal(0);
    if (searchQuery) doSearch(searchQuery, filters);
  };

  const handleFiltersReset = () => {
    setFilters(EMPTY_ADV_FILTERS); setActiveFilters(EMPTY_ADV_FILTERS);
    setFichesPage(0); cursorsRef.current = [null];
    setFiches([]); setFichesTotal(0);
    if (searchQuery) doSearch(searchQuery, EMPTY_ADV_FILTERS);
  };

  const goFichesPage = (p: number) => {
    if (p > 0 && cursorsRef.current[p] === undefined) return;
    setFichesPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center"><Loader className="!size-8" /></div>
    </div>
  );

  if (error || !article) return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <p className="text-6xl mb-6">📜</p>
        <h1 className="text-2xl font-bold mb-2">{t('articles.not_found_title')}</h1>
        <p className="text-muted-foreground mb-8">
          {nodeId
            ? <Trans t={t} i18nKey="articles.detail.not_found_node" components={{ mono: <span className="font-mono">{nodeId}</span> }} />
            : <Trans t={t} i18nKey="articles.detail.not_found_code" components={{ num: <span className="font-mono">{numero}</span>, code: <span className="font-mono">{slugCode}</span> }} />
          }
        </p>
        <Link to="/" className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium">
          {t('articles.back_to_search')}
        </Link>
      </div>
    </div>
  );

  const pageTitle = `Art. ${article.numero} ${article.code} | SmartLawyer`;
  const pageDesc  = article.section_summary?.slice(0, 160) || article.text?.slice(0, 160) || '';
  const canonical = `${SITE_URL}${article.canonical_url || `/articles/${article.slug_code}/${article.numero}`}`;

  const hasNextCursor    = cursorsRef.current[fichesPage + 1] !== undefined;
  const totalFichesPages = Math.ceil(fichesTotal / LIMIT);
  const displayFiches    = searchDone ? searchResults : fiches;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <script type="application/ld+json">{JSON.stringify({
          '@context':            'https://schema.org',
          '@type':               'Legislation',
          name:                  `Article ${article.numero} — ${article.code}`,
          legislationIdentifier: article.legiarti_id,
          url:                   canonical,
          publisher: { '@type': 'Organization', name: 'SmartLawyer', url: SITE_URL },
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        {/* ── Fil d'ariane ── */}
        <Breadcrumb items={[
          { label: t('articles.home'), to: '/' },
          { label: article.code,    to: `/articles/${article.slug_code}` },
          { label: `Art. ${article.numero}` },
        ]} />

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* En-tête */}
          <div className="mb-10 pb-10 border-b">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1 rounded-full font-mono font-medium">
                {article.code}
              </span>
              {article.nature && (
                <span className="text-xs bg-muted text-muted-foreground px-3 py-1 rounded-full">
                  {article.nature}
                </span>
              )}
              {fichesTotal > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-3 py-1 rounded-full font-medium">
                  {t('articles.detail.cited_in', { count: fichesTotal, formatted: fichesTotal.toLocaleString(locale) })}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('articles.heading', { numero: article.numero })}</h1>

            {article.en_vigueur === false && article.date_debut && article.date_fin && (
              <div role="note" className="mb-4 max-w-2xl rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                <p className="font-semibold">
                  {t('articles.detail.version_past', {
                    from: new Date(article.date_debut).toLocaleDateString(locale),
                    to:   new Date(article.date_fin).toLocaleDateString(locale),
                  })}
                </p>
                <p className="mt-1">
                  {t('articles.detail.version_past_hint')}{' '}
                  <Link to={`/articles/${article.slug_code}/${encodeURIComponent(article.numero)}`} className="underline font-medium">
                    {t('articles.detail.version_current_link')}
                  </Link>
                </p>
              </div>
            )}
            {article.en_vigueur === true && article.slug_code === 'loi-informatique-et-libertes' && (
              <p role="note" className="mb-4 max-w-2xl text-xs text-muted-foreground">
                {t('articles.detail.lil_renumbering')}
              </p>
            )}

            {article.classification && (
              <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl"
                dangerouslySetInnerHTML={{
                  __html: article.classification.replace(/&gt;/g, ' › ').replace(/>/g, ' › ')
                }} />
            )}

            {article.legifrance_url && (
              <a href={article.legifrance_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary hover:text-primary/80 font-medium transition-colors">
                {t('articles.detail.view_legifrance')}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>

          {/* Onglets */}
          <div className="flex gap-1 mb-10 border-b">
            {([
              ['text',      `📜 ${t('articles.detail.tab_text')}`],
              ['questions', `❓ ${t('articles.detail.tab_questions')}${article.questions?.length ? ` (${article.questions.length})` : ''}`],
              ['fiches',    `⚖️ ${t('articles.detail.tab_decisions')}${fichesTotal ? ` (${fichesTotal.toLocaleString(locale)})` : ''}`],
            ] as [ArticleTab, string][]).map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`px-5 py-2.5 text-sm font-medium rounded-t-lg -mb-px border transition-colors ${
                  activeTab === key
                    ? 'bg-background border-border border-b-background text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                {label}
              </button>
            ))}
          </div>

          {/* ── Texte ── */}
          {activeTab === 'text' && (
            <div className="space-y-8">
              <div className="rounded-xl border bg-muted/20 p-6">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{article.text}</p>
              </div>
              {article.section_summary && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-primary mb-3">{t('articles.detail.section_summary')}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{article.section_summary}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Questions ── */}
          {activeTab === 'questions' && (
            <div>
              {!article.questions?.length ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-4xl mb-4">❓</p>
                  <p>{t('articles.detail.no_questions')}</p>
                </div>
              ) : (
                <ol className="space-y-4">
                  {article.questions.map((q, i) => (
                    <li key={i} className="flex gap-4 p-5 rounded-xl border bg-card">
                      <span className="text-sm font-bold text-primary shrink-0 w-6 text-right">{i + 1}.</span>
                      <p className="text-sm leading-relaxed">{q}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* ── Décisions citantes ── */}
          {activeTab === 'fiches' && (
            <div>
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                      placeholder={t('articles.detail.search_placeholder', { formatted: fichesTotal.toLocaleString(locale) })}
                      className="w-full h-10 pl-9 pr-4 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <button type="submit" disabled={!searchInput.trim() || searching}
                    className="px-4 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-40 transition-colors">
                    {searching
                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : t('common.search')}
                  </button>
                  {searchDone && (
                    <button type="button" onClick={handleSearchReset}
                      className="px-3 h-10 rounded-xl border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
                      ✕
                    </button>
                  )}
                </div>
              </form>

              <div className="mb-6">
                <div className="flex items-center justify-between px-1">
                  <button onClick={() => setShowFilters(v => !v)}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                    <svg className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    {t('articles.detail.advanced_filters')}
                    {hasActiveFilters && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary inline-block" />}
                  </button>
                  {hasActiveFilters && (
                    <button onClick={handleFiltersReset}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                      {t('articles.detail.clear_filters')}
                    </button>
                  )}
                </div>
                {showFilters && (
                  <AdvancedSearchPanel
                    filters={filters} onChange={setFilters}
                    onApply={handleFiltersApply} onReset={handleFiltersReset} compact />
                )}
              </div>

              {searching ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <Loader className="!size-8" />
                  <p className="text-sm text-muted-foreground">{t('articles.detail.searching')}</p>
                </div>
              ) : loadingFiches ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <Loader className="!size-8" />
                  <p className="text-sm text-muted-foreground">{t('articles.detail.loading_decisions')}</p>
                </div>
              ) : displayFiches.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-4xl mb-4">⚖️</p>
                  {searchDone
                    ? <p><Trans t={t} i18nKey="articles.detail.no_match" components={{ q: <em>{t('articles.quoted', { query: searchQuery })}</em> }} /></p>
                    : <p>{t('articles.detail.no_citing')}</p>
                  }
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-6">
                    {searchDone ? (
                      <>
                        <Trans t={t} i18nKey="articles.results_for" count={searchTotal} components={{
                          strong: <strong className="text-foreground">{searchTotal}</strong>,
                          q: <em className="text-foreground">{t('articles.quoted', { query: searchQuery })}</em>,
                        }} />
                        {searchTime != null && (
                          <span className="ml-2 text-xs opacity-60">
                            ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                          </span>
                        )}
                        <span className="ml-2 text-xs opacity-60">{t('articles.detail.among_citing', { numero: article.numero })}</span>
                      </>
                    ) : (
                      <>
                        <Trans t={t} i18nKey="articles.detail.citing_count" count={fichesTotal} components={{
                          strong: <strong className="text-foreground">{fichesTotal.toLocaleString(locale)}</strong>,
                          art: <span className="font-mono font-medium text-foreground">Art. {article.numero} {article.code}</span>,
                        }} />
                        {fichesPage > 0 && <span className="ml-2 text-muted-foreground/60">{t('articles.page_suffix', { page: fichesPage + 1 })}</span>}
                      </>
                    )}
                  </p>

                  <div className="space-y-3">
                    {displayFiches.map(f => <FicheCard key={f.id} fiche={f} showScore={searchDone} />)}
                  </div>

                  {!searchDone && (fichesPage > 0 || hasNextCursor) && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button disabled={fichesPage === 0 || loadingFiches}
                        onClick={() => goFichesPage(fichesPage - 1)}
                        className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                        ← {t('common.previous')}
                      </button>
                      <span className="text-sm text-muted-foreground px-3">
                        {t('articles.page', { page: fichesPage + 1 })}
                        {totalFichesPages > 1 && ` / ~${totalFichesPages}`}
                      </span>
                      <button disabled={!hasNextCursor || loadingFiches}
                        onClick={() => goFichesPage(fichesPage + 1)}
                        className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                        {t('common.next')} →
                      </button>
                    </div>
                  )}

                  {searchDone && searchTotal > searchResults.length && (
                    <p className="text-center text-xs text-muted-foreground pt-6">
                      {t('articles.detail.showing_top', { shown: searchResults.length, total: searchTotal })}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </main>

        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}